import {
  Component,
  HostListener,
  Inject,
  OnDestroy,
  OnInit,
  PLATFORM_ID
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { Subject, Subscription } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';

import { CartAuthGateService } from '../../../../core/services/cart-auth-gate.service';
import { MegacableLoginError } from '../../../../core/models/megacable-login.model';
import { MegacableAuthService } from '../../../../core/services/megacable-auth.service';
import { ProductService } from '../../../services/product.service';

@Component({
  selector: 'app-cart-auth-modal',
  templateUrl: './cart-auth-modal.component.html',
  styleUrls: ['./cart-auth-modal.component.scss']
})
export class CartAuthModalComponent implements OnInit, OnDestroy {

  isOpen = false;
  hidePassword = true;
  loading = false;

  readonly loginForm: FormGroup;

  private readonly _destroy$ = new Subject<void>();
  private _loginSub?: Subscription;

  constructor(
    @Inject(PLATFORM_ID) private readonly platformId: Object,
    private readonly fb: FormBuilder,
    private readonly cartAuthGate: CartAuthGateService,
    private readonly megacableAuth: MegacableAuthService,
    private readonly productService: ProductService,
    private readonly router: Router,
    private readonly toastrService: ToastrService
  ) {
    this.loginForm = this.fb.group({
      username: ['', Validators.required],
      password: ['', [Validators.required, Validators.maxLength(16)]]
    });
  }

  ngOnInit(): void {
    this.cartAuthGate.open$.pipe(takeUntil(this._destroy$)).subscribe((open) => {
      this.isOpen = open;
      this.syncBodyScroll(open);
      if (open) {
        this._prepareFormForOpen();
      } else {
        this._resetFormState();
      }
    });
  }

  ngOnDestroy(): void {
    this._loginSub?.unsubscribe();
    this.syncBodyScroll(false);
    this._destroy$.next();
    this._destroy$.complete();
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.isOpen && !this.loading) {
      this.close();
    }
  }

  close(): void {
    if (this.loading) {
      return;
    }
    this.cartAuthGate.close();
  }

  togglePassword(): void {
    this.hidePassword = !this.hidePassword;
  }

  onLoginSubmit(): void {
    if (this.loading) {
      return;
    }

    if (this.loginForm.invalid) {
      this.loginForm.markAllAsTouched();
      return;
    }

    this.loading = true;
    this.loginForm.disable();

    const { username, password } = this.loginForm.getRawValue();

    this._loginSub?.unsubscribe();
    this._loginSub = this.megacableAuth
      .signInAndLoadProfile({ username, password })
      .pipe(
        finalize(() => {
          this.loading = false;
          this.loginForm.enable();
        })
      )
      .subscribe({
        next: () => {
          const intent = this.cartAuthGate.takePendingIntent();

          if (intent?.type === 'addToCart') {
            this.productService.addToCartAfterAuth(intent.product);
          } else if (intent?.type === 'buyNow') {
            if (this.productService.addToCartAfterAuth(intent.product)) {
              this.router.navigate(['/shop/checkout']);
            }
          } else if (intent?.type === 'checkout') {
            this.router.navigate(['/shop/checkout']);
          }

          this._resetFormState();
        },
        error: (error: MegacableLoginError | unknown) => {
          const message =
            error instanceof MegacableLoginError
              ? error.userMessage
              : 'No fue posible iniciar sesión. Inténtalo nuevamente.';
          this.toastrService.error(message);
        }
      });
  }

  private _prepareFormForOpen(): void {
    this.loading = false;
    this.hidePassword = true;
    this.loginForm.enable();
  }

  private _resetFormState(): void {
    this.loginForm.reset();
    this.loginForm.enable();
    this.hidePassword = true;
    this.loading = false;
  }

  private syncBodyScroll(open: boolean): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    document.documentElement.style.overflow = open ? 'hidden' : '';
  }
}
